
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Button, IconButton } from '../ui-kitNew/buttonNew'
import { Card } from '../ui-kitNew/cardNew'
import { Field, Input } from '../ui-kitNew/controlsNew'
import { MultiSelect } from '../ui-kitNew/multi-selectNew'
import { FilterIcon, RotateIcon, SearchIcon, SlidersIcon, XIcon } from '../ui-kitNew/iconsNew'
import { inferObjectFields, resolveField } from '../../lib/reportNew/fieldsNew'
import { describeCondition } from '../../lib/reportNew/filtersNew'
import { activeFilterChips, activeFilterCount, createFilterState } from '../../lib/reportNew/filter-stateNew'
import { FilterBuilder, useFieldOptions } from './filter-builderNew'
import { DateRangePicker } from './date-range-pickerNew'
import Modal from '../common/Modal/Modal'
import ButtonApp from '../common/Button/Button'
import { esCampoZona, ocultarZona, rolDelToken } from '../../utils/permisos'

function QuickFilter({ rows, fieldKey, value, onChange, fields }) {
  const { t } = useTranslation()
  const field = resolveField(fieldKey, fields)
  const options = useFieldOptions(rows, fieldKey, fields)
  if (!field) return null

  if (field.type === 'dateRange') {
    return (
      <Field label={field.label}>
        <DateRangePicker
          value={value || []}
          onChange={onChange}
          placeholder={t('ui.filters.allDates')}
        />
      </Field>
    )
  }

  return (
    <Field label={field.label}>
      <MultiSelect
        options={options}
        value={value || []}
        onChange={onChange}
        placeholder={t('ui.filters.allCount', { count: options.length })}
      />
    </Field>
  )
}

/**
 * Barra de filtrado global: búsqueda libre, filtros rápidos por campo y
 * constructor avanzado. Los chips permiten ver y quitar cada filtro activo.
 * `fields` permite inyectar los metadatos propios del consumidor (p. ej. los
 * filterFields de una tabla); si no se indica se usan los campos globales.
 */
export function FilterBar({ rows, state, onChange, resultCount, totalCount, fields, showAdvanced = true, headerExtra = null, excludeFields = [] }) {
  const { t } = useTranslation()
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [draft, setDraft] = useState(state.advanced)

  // Paridad con el modal del kit: cierre con Escape y bloqueo de scroll,
  // aplicados solo a esta ventana (el Modal común no se modifica).
  useEffect(() => {
    if (!advancedOpen) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setAdvancedOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [advancedOpen])

  const count = activeFilterCount(state)
  const chips = activeFilterChips(state, fields)

  /* Fuera del Administrador global, si todo lo que se ve es de una zona (el
     backend ya recorta a la del usuario), el filtro de Zona sobra: ni rápido ni
     avanzado. La usan tanto las tablas como las pantallas de informes. */
  const sinZona = ocultarZona(rolDelToken(), rows)
  const quickFields = Object.keys(state.quick).filter((key) => !(sinZona && esCampoZona(key)))

  // Filtros avanzados: campos declarados + inferidos del objeto, pero SIN los
  // que ya aparecen como filtro rápido en la barra (quickFields) ni los
  // excluidos explícitamente (excludeFields).
  const advancedFields = useMemo(() => {
    const excluded = new Set([...excludeFields, ...quickFields, ...(sinZona ? ['codZona', 'zona'] : [])])
    const seen = new Set()
    const merged = []
    ;(fields || []).forEach((field) => {
      if (excluded.has(field.key) || seen.has(field.key)) return
      seen.add(field.key)
      merged.push(field)
    })
    inferObjectFields(rows).forEach((field) => {
      if (excluded.has(field.key) || seen.has(field.key)) return
      seen.add(field.key)
      merged.push(field)
    })
    return merged
  }, [fields, rows, excludeFields, quickFields, sinZona])

  const removeChip = (chip) => {
    if (chip.kind === 'search') onChange({ ...state, search: '' })
    if (chip.kind === 'quick') onChange({ ...state, quick: { ...state.quick, [chip.field]: [] } })
    if (chip.kind === 'advanced') {
      onChange({
        ...state,
        advanced: {
          ...state.advanced,
          conditions: state.advanced.conditions.filter((item) => item.id !== chip.conditionId),
        },
      })
    }
  }

  const openAdvanced = () => {
    setDraft(state.advanced)
    setAdvancedOpen(true)
  }

  return (
    <Card className="px-[20px] py-[20px]">
      <div className="flex flex-col gap-[16px]">
        <div className="flex flex-wrap items-center gap-[12px]">
          <span className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <FilterIcon size={15} />
            </span>
            {t('ui.filters.filterData')}
          </span>

          <span className="text-[12.5px] text-muted-foreground">
            {t('ui.filters.recordsCount', {
              shown: resultCount.toLocaleString('es-ES'),
              total: totalCount.toLocaleString('es-ES'),
            })}
          </span>

          <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
            <div className="relative min-w-0 flex-1 sm:flex-none">
              <SearchIcon
                size={15}
                className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                size="md"
                value={state.search}
                onChange={(event) => onChange({ ...state, search: event.target.value })}
                placeholder={t('ui.filters.searchAll')}
                aria-label={t('ui.filters.searchAllAria')}
                className="pl-8 sm:w-[224px]"
              />
            </div>
            {showAdvanced && (
              <Button variant="outline" size="md" onClick={openAdvanced}>
                <SlidersIcon size={15} />
                {t('ui.filters.advanced')}
                {state.advanced.conditions.length > 0 && (
                  <span className="ml-0.5 rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                    {state.advanced.conditions.length}
                  </span>
                )}
              </Button>
            )}
            {count > 0 && (
              <IconButton
                label={t('ui.filters.resetAll')}
                size="icon-lg"
                onClick={() => onChange(createFilterState(quickFields))}
              >
                <RotateIcon size={17} />
              </IconButton>
            )}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {quickFields.map((fieldKey) => (
            <QuickFilter
              key={fieldKey}
              rows={rows}
              fieldKey={fieldKey}
              value={state.quick[fieldKey]}
              fields={fields}
              onChange={(values) =>
                onChange({ ...state, quick: { ...state.quick, [fieldKey]: values } })
              }
            />
          ))}
          {headerExtra && (
            <div className="flex flex-col justify-end">{headerExtra}</div>
          )}
        </div>

        {chips.length > 0 && (
          <ul className="flex flex-wrap items-center gap-1.5 border-t border-border pt-3">
            {chips.map((chip) => {
              const condition = state.advanced.conditions.find(
                (item) => item.id === chip.conditionId
              )
              const label = chip.label || (condition ? describeCondition(condition, fields) : '')
              return (
                <li key={chip.id}>
                  <span className="inline-flex max-w-[280px] items-center gap-1 rounded-full bg-primary-soft py-1 pr-1 pl-2.5 text-[12px] font-medium text-primary">
                    <span className="truncate">{label}</span>
                    <button
                      type="button"
                      onClick={() => removeChip(chip)}
                      aria-label={t('ui.filters.removeFilter', { label })}
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full hover:bg-primary/20"
                    >
                      <XIcon size={11} />
                    </button>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {createPortal(
        <Modal
          isOpen={advancedOpen}
          onClose={() => setAdvancedOpen(false)}
          title={t('ui.filters.advanced')}
          className="modal--advanced-filters"
        >
          <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">
            {t('ui.filters.advancedHelp')}
          </p>
          <FilterBuilder rows={rows} fields={advancedFields} group={draft} onChange={setDraft} />
          <div className="mt-5 flex items-center justify-end gap-3">
            <ButtonApp
              variant="outlined"
              onClick={() => setAdvancedOpen(false)}
              disabled={false}
            >
              {t('common.cancel')}
            </ButtonApp>
            <ButtonApp
              variant="filled"
              onClick={() => {
                onChange({ ...state, advanced: draft })
                setAdvancedOpen(false)
              }}
              disabled={false}
            >
              {t('ui.filters.applyFilters')}
            </ButtonApp>
          </div>
        </Modal>,
        document.body
      )}
    </Card>
  )
}
